import { Box, Grid, HStack, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { EmptyState, IconButton } from 'Atoms';
import { ContentLayout, ContentHeader, Loader, Select } from 'Molecules';
import { ActivitiesIllustration, Typography } from 'Tokens';
import { CompanyBothIcon, CompanyBottomUpIcon, CompanyTopDownIcon } from 'Tokens/Icons/Custom';
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetSubsidiaryStandardsOverview } from './AssessmentOverview.hooks';
import {
  EsrsAssessmentDocument_,
  EsrsAssessmentFieldsFragment_,
  GetFlaggedTargetsQuery_,
  useUpdateAssessmentCompanyProjectLeaderMutation,
} from 'models';
import { FlaggedTargets } from './FlaggedTargets';
import { AssessmentProgress, StandardProgress } from './StandardProgress';
import { EsrsCategoryMap } from '../../GroupAssessment/GroupAssessmentOverview.hooks';
import {
  InfoCard,
  MetricInfoCard,
} from '../../DisclosureRequirements/StandardPage/StandardPageComponents/DisclosureRequirementHeader';
import { UserAvatar } from 'Organisms';
import { EditIcon } from 'Tokens/Icons/Data';
import { ResponsibleSelector, ToolbarToast } from 'Molecules/TaskToolbar';

export enum MaterialTopicFilter {
  ALL = 'all',
  MATERIAL = 'material',
  NOT_MATERIAL = 'not-material',
}

export const MATERIAL_TOPIC_FILTER_OPTIONS = [
  {
    label: 'All available data',
    description: 'Topics that were assessed as material by a parent company and by subsidiaries',
    icon: <CompanyBothIcon color="text.default" />,
    value: MaterialTopicFilter.ALL,
  },
  {
    label: 'Only material topics',
    description: 'Topics that were assessed as material by a parent company (this company)',
    icon: <CompanyTopDownIcon color="text.default" />,
    value: MaterialTopicFilter.MATERIAL,
  },
  {
    label: 'Only not material topics',
    description: 'Topics that were assessed as material by subsidiaries',
    icon: <CompanyBottomUpIcon color="text.default" />,
    value: MaterialTopicFilter.NOT_MATERIAL,
  },
];

export const EsrsAssessmentOverview = ({
  esrsAssessmentId,
  companyName,
  targets,
  projectLeader,
  parentAssessmentId,
  isGroup,
}: {
  esrsAssessmentId: string;
  companyName: string;
  targets?: GetFlaggedTargetsQuery_['targets'];
  projectLeader: EsrsAssessmentFieldsFragment_['projectLeader'];
  parentAssessmentId?: string;
  isGroup: boolean;
}) => {
  const navigate = useNavigate();
  const [isOwnerHovered, setIsOwnerHovered] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const toast = useToast();
  const [filter, setFilter] = useState('all');

  const { categories, loading } = useGetSubsidiaryStandardsOverview(filter, esrsAssessmentId);
  const [updateProjectLeader] = useUpdateAssessmentCompanyProjectLeaderMutation();

  const noStandardsToShow = useMemo(
    () => categories.filter((c) => c.materialStandards.length).length === 0,
    [categories]
  );

  const hasNonMaterialStandards = useMemo(
    () =>
      categories.some((c) =>
        c.materialStandards.some((std) => std.isMaterialForGroup && !std.isMaterialForSubsidiary)
      ),
    [categories]
  );

  const filteredCategories = useMemo(() => {
    const filterStandard = (std: EsrsCategoryMap['materialStandards'][number]) => {
      if (filter === 'all') return true;
      if (filter === 'not-material') {
        return std.isMaterialForGroup && !std.isMaterialForSubsidiary;
      } else if (filter === 'material') {
        return std.isMaterialForSubsidiary;
      }
    };
    return categories.map((cat) => ({
      ...cat,
      materialStandards: cat.materialStandards.filter(filterStandard),
    }));
  }, [categories, filter]);

  const flaggedStandards = useMemo(
    () => targets?.map((target) => target.materialStandard.standardRef) ?? [],
    [targets]
  );

  const handleUpdateProjectLeader = (projectLeaderId: string, ownerName?: string) => {
    updateProjectLeader({
      variables: { id: esrsAssessmentId, projectLeaderId },
      refetchQueries: [EsrsAssessmentDocument_],
    }).then(() => {
      toast.close('loadingToast');
      toast({
        position: 'bottom',
        duration: 3000,
        render: () => <ToolbarToast text={`Assigned ${ownerName} as responsible`} isStandalone />,
      });
    });
  };

  if (loading) return <Loader />;

  return (
    <>
      {noStandardsToShow ? (
        <Box w="100%" h="100%" p="16px">
          <EmptyState
            title="Start with materiality assessment"
            description="Add material standards, disclosure requirements and metrics to get started"
            icon={<ActivitiesIllustration boxSize="120px" />}
            callToAction={{
              text: 'Begin assessment',
              variant: 'primary',
              onClick: () => navigate('settings'),
            }}
          />
        </Box>
      ) : (
        <ContentLayout
          isLoading={loading}
          variant="inline"
          paddingTop="32px"
          paddingBottom="56px"
          header={<ContentHeader title={`${companyName} overview`} size="md" />}
          noHeader
        >
          {!!targets?.length && <FlaggedTargets targets={targets} />}
          <HStack w="100%" gap="8px" align="stretch" mt="16px" mb="8px">
            <InfoCard
              title="Data collection progress"
              content={
                <AssessmentProgress isGroup={isGroup} parentAssessmentId={parentAssessmentId} />
              }
            />
            <MetricInfoCard
              title="Main responsible"
              onMouseEnter={() => setIsOwnerHovered(true)}
              onMouseLeave={() => setIsOwnerHovered(false)}
              content={
                <HStack spacing="8px">
                  <UserAvatar
                    user={projectLeader ?? undefined}
                    size="sm"
                    boxSize="28px"
                    border="none"
                  />
                  <Typography variant="h3">{projectLeader?.displayName}</Typography>
                  {isOwnerHovered && (
                    <IconButton
                      aria-label="edit-dr-owner"
                      icon={<EditIcon />}
                      borderRadius="50%"
                      variant="ghost"
                      onClick={onOpen}
                      size="sm"
                    />
                  )}
                </HStack>
              }
            />
          </HStack>
          <ResponsibleSelector
            isOpen={isOpen}
            onClose={onClose}
            onSelect={handleUpdateProjectLeader}
            isStandalone
          />
          <VStack alignItems="start" spacing="40px" mt="24px">
            {hasNonMaterialStandards && (
              <Box width="280px">
                <Select<{ value: string; label: string }>
                  value={{
                    label:
                      MATERIAL_TOPIC_FILTER_OPTIONS.find((v) => v.value === filter)?.label ?? '',
                    value: filter,
                  }}
                  onChange={(value) => setFilter(value?.value ? value.value : 'all')}
                  options={MATERIAL_TOPIC_FILTER_OPTIONS.map((currOption) => {
                    return {
                      label: currOption?.label ?? '',
                      value: currOption.value,
                    };
                  })}
                  size="md"
                />
              </Box>
            )}
            {filteredCategories.map((category, categoryIndex) => (
              <Fragment key={category.categoryRef}>
                {category.materialStandards.length > 0 && (
                  <VStack key={category.categoryRef} alignItems="start" width="100%">
                    <Typography variant="overline">{category.categoryName}</Typography>
                    <Grid templateColumns="repeat(3, 1fr)" overflow="visible" gap="12px" w="full">
                      {category.materialStandards.map((standard, standardIndex, standardsArr) => (
                        <StandardProgress
                          standard={standard}
                          standardIndex={standardIndex}
                          categoryIndex={categoryIndex}
                          standardLength={standardsArr.length}
                          key={standard.standardRef}
                          isFlagged={flaggedStandards?.includes(standard.standardRef)}
                          parentAssessmentId={standard.parentEsrsAssessmentId}
                          drRefs={standard.drRefs}
                        />
                      ))}
                    </Grid>
                  </VStack>
                )}
              </Fragment>
            ))}
          </VStack>
        </ContentLayout>
      )}
    </>
  );
};
